import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, db } from '../firebase';
import './SettingsPage.css';

const SettingsPage = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState({
        emailNotifications: true,
        bookingReminders: true,
        promotionalEmails: false,
        statusUpdates: true,
        darkMode: false,
        language: 'en',
        currency: 'INR',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        const fetchSettings = async () => {
            if (!currentUser) { setLoading(false); return; }
            try {
                const snap = await getDoc(doc(db, 'users', currentUser.uid));
                if (snap.exists() && snap.data().settings) {
                    setSettings(prev => ({ ...prev, ...snap.data().settings }));
                }
            } catch (err) {
                console.error(err);
            }
            setLoading(false);
        };
        fetchSettings();
    }, [currentUser]);

    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setErrorMsg('');
        setTimeout(() => setSuccessMsg(''), 4000);
    };

    const showError = (msg) => {
        setErrorMsg(msg);
        setSuccessMsg('');
        setTimeout(() => setErrorMsg(''), 5000);
    };

    const handleToggle = (key) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSelect = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'users', currentUser.uid), { settings }, { merge: true });
            showSuccess('Settings saved successfully!');
        } catch (err) {
            showError('Failed to save settings.');
        }
        setSaving(false);
    };

    const handleDeleteAccount = async () => {
        setDeleteError('');
        if (!deletePassword) { setDeleteError('Please enter your password.'); return; }
        setDeleteLoading(true);
        try {
            const isGoogleUser = currentUser.providerData[0]?.providerId === 'google.com';
            if (!isGoogleUser) {
                const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
                await reauthenticateWithCredential(auth.currentUser, credential);
            }
            await deleteUser(auth.currentUser);
            navigate('/');
        } catch (err) {
            setDeleteError(
                err.code === 'auth/wrong-password' ? 'Incorrect password.' :
                err.code === 'auth/too-many-requests' ? 'Too many attempts. Try again later.' :
                'Could not delete account: ' + err.message
            );
        }
        setDeleteLoading(false);
    };

    if (!currentUser) {
        return (
            <div className="st-page">
                <div className="container">
                    <div className="st-login-prompt">
                        <div className="st-lock-icon">🔒</div>
                        <h2>Login Required</h2>
                        <p>Please login to view your settings.</p>
                        <Link to="/login" className="st-btn st-btn-primary">Login Now</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="st-page">
            <div className="container">
                <div className="st-page-header">
                    <h1>Settings</h1>
                    <p>Customize your experience and manage your account</p>
                </div>

                {/* Alerts */}
                {successMsg && <div className="st-alert st-alert-success">✓ {successMsg}</div>}
                {errorMsg && <div className="st-alert st-alert-error">✕ {errorMsg}</div>}

                {loading ? (
                    <div className="st-loading"><div className="st-spinner-lg"></div><p>Loading settings...</p></div>
                ) : (
                    <div className="st-layout">
                        {/* Notifications Section */}
                        <section className="st-card">
                            <div className="st-card-header">
                                <div className="st-card-icon st-icon-notif">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
                                </div>
                                <div>
                                    <h3 className="st-card-title">Notifications</h3>
                                    <p className="st-card-desc">Control which notifications you receive</p>
                                </div>
                            </div>
                            <div className="st-toggles">
                                <ToggleRow
                                    label="Email Notifications"
                                    desc="Get updates delivered to your email"
                                    checked={settings.emailNotifications}
                                    onToggle={() => handleToggle('emailNotifications')}
                                />
                                <ToggleRow
                                    label="Booking Reminders"
                                    desc="Reminders before your scheduled service"
                                    checked={settings.bookingReminders}
                                    onToggle={() => handleToggle('bookingReminders')}
                                />
                                <ToggleRow
                                    label="Status Updates"
                                    desc="Notify when booking status changes"
                                    checked={settings.statusUpdates}
                                    onToggle={() => handleToggle('statusUpdates')}
                                />
                                <ToggleRow
                                    label="Promotional Emails"
                                    desc="Offers, discounts and weekly deals"
                                    checked={settings.promotionalEmails}
                                    onToggle={() => handleToggle('promotionalEmails')}
                                />
                            </div>
                        </section>

                        {/* Preferences Section */}
                        <section className="st-card">
                            <div className="st-card-header">
                                <div className="st-card-icon st-icon-pref">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                                </div>
                                <div>
                                    <h3 className="st-card-title">Preferences</h3>
                                    <p className="st-card-desc">Language, currency, and display options</p>
                                </div>
                            </div>
                            <div className="st-prefs">
                                <div className="st-pref-row">
                                    <div>
                                        <p className="st-pref-label">Language</p>
                                        <p className="st-pref-desc">App display language</p>
                                    </div>
                                    <select
                                        className="st-select"
                                        value={settings.language}
                                        onChange={e => handleSelect('language', e.target.value)}
                                    >
                                        <option value="en">English</option>
                                        <option value="hi">हिन्दी (Hindi)</option>
                                        <option value="ta">தமிழ் (Tamil)</option>
                                        <option value="te">తెలుగు (Telugu)</option>
                                        <option value="mr">मराठी (Marathi)</option>
                                        <option value="kn">ಕನ್ನಡ (Kannada)</option>
                                    </select>
                                </div>
                                <div className="st-pref-row">
                                    <div>
                                        <p className="st-pref-label">Currency</p>
                                        <p className="st-pref-desc">Currency for price display</p>
                                    </div>
                                    <select
                                        className="st-select"
                                        value={settings.currency}
                                        onChange={e => handleSelect('currency', e.target.value)}
                                    >
                                        <option value="INR">₹ INR</option>
                                        <option value="USD">$ USD</option>
                                        <option value="EUR">€ EUR</option>
                                        <option value="GBP">£ GBP</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        {/* Account Info Section */}
                        <section className="st-card">
                            <div className="st-card-header">
                                <div className="st-card-icon st-icon-account">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                                </div>
                                <div>
                                    <h3 className="st-card-title">Account</h3>
                                    <p className="st-card-desc">Your account details and sign-in information</p>
                                </div>
                            </div>
                            <div className="st-account-info">
                                <div className="st-info-row">
                                    <span className="st-info-label">Email</span>
                                    <span className="st-info-value">{currentUser.email}</span>
                                </div>
                                <div className="st-info-row">
                                    <span className="st-info-label">Sign-in Method</span>
                                    <span className="st-info-value">
                                        {currentUser.providerData[0]?.providerId === 'google.com'
                                            ? <><span className="st-provider-badge st-google">G</span> Google</>
                                            : <><span className="st-provider-badge st-email">✉</span> Email & Password</>
                                        }
                                    </span>
                                </div>
                                <div className="st-info-row">
                                    <span className="st-info-label">Email Verified</span>
                                    <span className={`st-info-value ${currentUser.emailVerified ? 'st-verified' : 'st-unverified'}`}>
                                        {currentUser.emailVerified ? '✓ Verified' : '✗ Not Verified'}
                                    </span>
                                </div>
                                <div className="st-info-row">
                                    <span className="st-info-label">Member Since</span>
                                    <span className="st-info-value">
                                        {currentUser.metadata?.creationTime
                                            ? new Date(currentUser.metadata.creationTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                                            : 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <div className="st-account-links">
                                <Link to="/profile" className="st-link-btn">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    Edit Personal Details
                                </Link>
                            </div>
                        </section>

                        {/* Save Button */}
                        <div className="st-save-bar">
                            <button className="st-btn st-btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? <><span className="st-spinner"></span> Saving...</> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Save Settings</>}
                            </button>
                        </div>

                        {/* Danger Zone */}
                        <section className="st-card st-danger-card">
                            <div className="st-card-header">
                                <div className="st-card-icon st-icon-danger">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                </div>
                                <div>
                                    <h3 className="st-card-title st-danger-title">Danger Zone</h3>
                                    <p className="st-card-desc">Irreversible account actions</p>
                                </div>
                            </div>

                            <div className="st-danger-actions">
                                <div className="st-danger-item">
                                    <div>
                                        <p className="st-danger-item-title">Sign Out</p>
                                        <p className="st-danger-item-desc">Sign out of your account on this device</p>
                                    </div>
                                    <button
                                        className="st-btn st-btn-outline"
                                        onClick={async () => { await logout(); navigate('/login'); }}
                                    >
                                        Sign Out
                                    </button>
                                </div>
                                <div className="st-danger-item">
                                    <div>
                                        <p className="st-danger-item-title st-delete-title">Delete Account</p>
                                        <p className="st-danger-item-desc">Permanently delete your account and all data. This cannot be undone.</p>
                                    </div>
                                    <button
                                        className="st-btn st-btn-danger"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        Delete Account
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="st-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="st-modal" onClick={e => e.stopPropagation()}>
                        <div className="st-modal-icon">⚠️</div>
                        <h3 className="st-modal-title">Delete Account?</h3>
                        <p className="st-modal-desc">
                            This will permanently delete your account and all associated data including bookings and saved addresses.
                            <strong> This action cannot be undone.</strong>
                        </p>

                        {currentUser.providerData[0]?.providerId !== 'google.com' && (
                            <div className="st-field">
                                <label>Enter your password to confirm</label>
                                <input
                                    type="password"
                                    value={deletePassword}
                                    onChange={e => setDeletePassword(e.target.value)}
                                    placeholder="Your current password"
                                    autoFocus
                                />
                            </div>
                        )}

                        {deleteError && <p className="st-delete-error">✕ {deleteError}</p>}

                        <div className="st-modal-actions">
                            <button className="st-btn st-btn-outline" onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(''); }}>
                                Cancel
                            </button>
                            <button className="st-btn st-btn-danger" onClick={handleDeleteAccount} disabled={deleteLoading}>
                                {deleteLoading ? <><span className="st-spinner st-spinner-white"></span> Deleting...</> : 'Yes, Delete My Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Toggle Row Component
const ToggleRow = ({ label, desc, checked, onToggle }) => (
    <div className="st-toggle-row">
        <div className="st-toggle-info">
            <p className="st-toggle-label">{label}</p>
            <p className="st-toggle-desc">{desc}</p>
        </div>
        <button
            type="button"
            className={`st-toggle ${checked ? 'st-toggle-on' : ''}`}
            onClick={onToggle}
            aria-label={label}
            role="switch"
            aria-checked={checked}
        >
            <span className="st-toggle-thumb"></span>
        </button>
    </div>
);

export default SettingsPage;
